/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  search: {
    all: ["search"] as const,
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Upload queue stats (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Is there local data? (cleanup button in profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (getThemesQuery without pagination; profile + list-dialog SHARE the cache). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
    /** Evidence photos of an answered item. */
    byAnswer: (answerId: string | undefined) => ["attachments", "answer", answerId] as const,
    /** Photos of a finding. */
    byFinding: (findingId: string | undefined) => ["attachments", "finding", findingId] as const,
    /** Signature (or other attachments) of an inspection. */
    byInspection: (inspectionId: string | undefined) =>
      ["attachments", "inspection", inspectionId] as const,
    /** Photos of ALL items in an inspection (validation/report). */
    forInspectionAnswers: (inspectionId: string | undefined) =>
      ["attachments", "inspection-answers", inspectionId] as const,
  },
  sites: {
    all: ["sites"] as const,
    /** `scope` = active org from the switcher (`null`/omitted = Personal / no scope). */
    list: (userId: string | undefined, scope?: string | null) =>
      ["sites", "list", userId, scope ?? null] as const,
    detail: (siteId: string | undefined) => ["sites", "detail", siteId] as const,
  },
  templates: {
    all: ["templates"] as const,
    list: (userId: string | undefined, scope?: string | null) =>
      ["templates", "list", userId, scope ?? null] as const,
    detail: (templateId: string | undefined) => ["templates", "detail", templateId] as const,
    items: (templateId: string | undefined) => ["templates", "items", templateId] as const,
    /** LOCAL version history of a template (bounded bucket). */
    versions: (templateId: string | undefined) => ["templates", "versions", templateId] as const,
    /** SEALED version of an inspection (API fetch for the report). */
    sealedVersion: (inspectionId: string | undefined) =>
      ["templates", "sealedVersion", inspectionId] as const,
  },
  inspections: {
    all: ["inspections"] as const,
    list: (userId: string | undefined, status?: string, scope?: string | null) =>
      ["inspections", "list", userId, status, scope ?? null] as const,
    detail: (inspectionId: string | undefined) => ["inspections", "detail", inspectionId] as const,
    answers: (inspectionId: string | undefined) =>
      ["inspections", "answers", inspectionId] as const,
    /** Counts for the dashboard (in progress / recently completed). */
    stats: (userId: string | undefined, scope?: string | null) =>
      ["inspections", "stats", userId, scope ?? null] as const,
  },
  findings: {
    all: ["findings"] as const,
    list: <T extends object>(options: T) => ["findings", "list", options] as const,
    detail: (findingId: string | undefined) => ["findings", "detail", findingId] as const,
    byInspection: (inspectionId: string | undefined) =>
      ["findings", "inspection", inspectionId] as const,
    /** Count of open findings by severity (dashboard). */
    openStats: (userId: string | undefined, scope?: string | null) =>
      ["findings", "openStats", userId, scope ?? null] as const,
  },
  activity: {
    all: ["activity"] as const,
    /** Local feed (activity_recent bucket). ALWAYS with userId: isolates cache between accounts. */
    feed: (userId: string | undefined) => ["activity", "feed", userId] as const,
    /** Local history of a specific entity. */
    entity: (userId: string | undefined, entityType: string, entityId: string) =>
      ["activity", "entity", userId, entityType, entityId] as const,
    /** Server-side verification of the hash chain. */
    verify: (userId: string | undefined) => ["activity", "verify", userId] as const,
  },
  gamification: {
    all: ["gamification"] as const,
    /** Game state (XP/level/streak/badges) derived from the activity feed. */
    player: (userId: string | undefined) => ["gamification", "player", userId] as const,
  },
  trash: {
    all: ["trash"] as const,
    inspections: (userId: string | undefined) => ["trash", "inspections", userId] as const,
    findings: (userId: string | undefined) => ["trash", "findings", userId] as const,
    templates: (userId: string | undefined) => ["trash", "templates", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  organizations: {
    /** Local (PowerSync) replica of my organizations. */
    localList: () => ["organizations", "local-list"] as const,
    /** Members (with public info) of an organization, from the local replica. */
    localMembers: (organizationId: string | undefined) =>
      ["organizations", "local-members", organizationId] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
    /**
     * The user's active sessions. Hierarchical on purpose: `onRevokeSuccess`
     * invalidates the `activeSessions` PREFIX without knowing the tab's `sessionId`
     * (P23/CC-60 · assignment C).
     */
    activeSessions: ["auth", "activeSessions"] as const,
    activeSessionsFor: (currentSessionId: string | undefined) =>
      ["auth", "activeSessions", currentSessionId] as const,
  },
};
